import { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Power, ArrowUp, ArrowDown, X } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import OnboardingEditor from '../../components/admin/OnboardingEditor';

const ScenarioManagement = () => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    difficulty: 'easy',
    characterRole: 'secretary',
    characterName: '',
    characterCompany: '',
    characterPersonality: '',
    objectives: [''],
    systemPrompt: '',
    voice: 'xenia',
    onboardingSteps: [],
  });

  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      const response = await api.get('/api/admin/scenarios?includeInactive=true');
      setScenarios(response.data.scenarios);
    } catch (error) {
      console.error('Error loading scenarios:', error);
      toast.error('Ошибка загрузки сценариев');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScenario = () => {
    setEditingScenario(null);
    setFormData({
      id: '',
      name: '',
      description: '',
      difficulty: 'easy',
      characterRole: 'secretary',
      characterName: '',
      characterCompany: '',
      characterPersonality: '',
      objectives: [''],
      systemPrompt: '',
      voice: 'xenia',
      onboardingSteps: [],
    });
    setShowModal(true);
  };

  const handleEditScenario = (scenario) => {
    setEditingScenario(scenario);
    setFormData({
      id: scenario.id,
      name: scenario.name,
      description: scenario.description || '',
      difficulty: scenario.difficulty,
      characterRole: scenario.character?.role || 'secretary',
      characterName: scenario.character?.name || '',
      characterCompany: scenario.character?.company || '',
      characterPersonality: scenario.character?.personality || '',
      objectives: scenario.objectives || [''],
      systemPrompt: scenario.systemPrompt || '',
      voice: scenario.voice || 'xenia',
      onboardingSteps: scenario.onboarding?.steps || [],
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.name.trim()) {
      toast.error('Название сценария обязательно');
      return;
    }

    if (!formData.id || !formData.id.trim()) {
      toast.error('ID сценария обязателен');
      return;
    }

    if (!formData.systemPrompt || !formData.systemPrompt.trim()) {
      toast.error('Системный промпт обязателен');
      return;
    }

    // Filter out empty objectives
    const validObjectives = formData.objectives.filter(obj => obj.trim() !== '');
    if (validObjectives.length === 0) {
      toast.error('Добавьте хотя бы одну цель');
      return;
    }

    try {
      const payload = {
        id: formData.id.trim(),
        name: formData.name.trim(),
        description: formData.description.trim(),
        difficulty: formData.difficulty,
        character: {
          role: formData.characterRole,
          name: formData.characterName.trim(),
          company: formData.characterCompany.trim(),
          personality: formData.characterPersonality.trim(),
        },
        objectives: validObjectives,
        systemPrompt: formData.systemPrompt.trim(),
        voice: formData.voice,
        evaluationCriteria: [], // Can be added later if needed
      };

      // Always include onboarding field when editing scenarios
      if (editingScenario) {
        // For existing scenarios, always include onboarding field
        payload.onboarding = { steps: formData.onboardingSteps };
      } else {
        // For new scenarios, only include onboarding if there are steps
        if (formData.onboardingSteps.length > 0) {
          payload.onboarding = { steps: formData.onboardingSteps };
        } else {
          payload.onboarding = null;
        }
      }

      if (editingScenario) {
        // Update scenario
        await api.put(`/api/admin/scenarios/${editingScenario.id}`, payload);
        toast.success('Сценарий обновлен');
      } else {
        // Create scenario
        await api.post('/api/admin/scenarios', payload);
        toast.success('Сценарий создан');
      }

      setShowModal(false);
      loadScenarios();
    } catch (error) {
      console.error('Error saving scenario:', error);
      const errorMessage = error.response?.data?.error || 'Ошибка сохранения сценария';
      toast.error(errorMessage);
    }
  };

  const handleToggleActive = async (scenario) => {
    try {
      await api.patch(`/api/admin/scenarios/${scenario.id}/toggle-active`);
      toast.success(scenario.is_active ? 'Сценарий деактивирован' : 'Сценарий активирован');
      loadScenarios();
    } catch (error) {
      console.error('Error toggling scenario:', error);
      toast.error('Ошибка изменения статуса');
    }
  };

  const handleDelete = async (scenario) => {
    if (!confirm(`Удалить сценарий "${scenario.name}"?`)) return;

    try {
      await api.delete(`/api/admin/scenarios/${scenario.id}`);
      toast.success('Сценарий удален');
      loadScenarios();
    } catch (error) {
      console.error('Error deleting scenario:', error);
      toast.error('Ошибка удаления');
    }
  };

  const handleMoveUp = async (scenario, index) => {
    if (index === 0) {
      toast.error('Сценарий уже в начале списка');
      return;
    }

    try {
      await api.patch(`/api/admin/scenarios/${scenario.id}/move-up`);
      toast.success('Сценарий перемещен вверх');
      loadScenarios();
    } catch (error) {
      console.error('Error moving scenario up:', error);
      toast.error(error.response?.data?.error || 'Ошибка перемещения');
    }
  };

  const handleMoveDown = async (scenario, index) => {
    if (index === scenarios.length - 1) {
      toast.error('Сценарий уже в конце списка');
      return;
    }

    try {
      await api.patch(`/api/admin/scenarios/${scenario.id}/move-down`);
      toast.success('Сценарий перемещен вниз');
      loadScenarios();
    } catch (error) {
      console.error('Error moving scenario down:', error);
      toast.error(error.response?.data?.error || 'Ошибка перемещения');
    }
  };

  const addObjective = () => {
    setFormData({ ...formData, objectives: [...formData.objectives, ''] });
  };

  const removeObjective = (index) => {
    const newObjectives = formData.objectives.filter((_, i) => i !== index);
    setFormData({ ...formData, objectives: newObjectives });
  };

  const updateObjective = (index, value) => {
    const newObjectives = [...formData.objectives];
    newObjectives[index] = value;
    setFormData({ ...formData, objectives: newObjectives });
  };

  const getDifficultyBadge = (difficulty) => {
    const badges = {
      easy: { class: 'bg-green-100 text-green-700', label: 'Легкий' },
      medium: { class: 'bg-yellow-100 text-yellow-700', label: 'Средний' },
      hard: { class: 'bg-orange-100 text-orange-700', label: 'Сложный' },
      expert: { class: 'bg-red-100 text-red-700', label: 'Эксперт' },
    };
    return badges[difficulty] || badges.easy;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Управление сценариями</h1>
          <p className="text-gray-600 mt-1">Всего сценариев: {scenarios.length}</p>
        </div>
        <button
          onClick={handleCreateScenario}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Создать сценарий
        </button>
      </div>

      {/* Scenarios Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {scenarios.map((scenario, index) => {
          const difficultyBadge = getDifficultyBadge(scenario.difficulty);
          return (
            <div
              key={scenario.id}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {scenario.name}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {scenario.description}
                  </p>
                </div>
                {scenario.is_active ? (
                  <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                    Активен
                  </span>
                ) : (
                  <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                    Неактивен
                  </span>
                )}
              </div>

              {/* Meta */}
              <div className="flex items-center gap-2 mb-4">
                <span className={`px-2 py-1 text-xs font-medium rounded ${difficultyBadge.class}`}>
                  {difficultyBadge.label}
                </span>
                {scenario.character?.role && (
                  <span className="text-xs text-gray-500">
                    👤 {scenario.character.role}
                  </span>
                )}
              </div>

              {/* Objectives */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-400 uppercase mb-2">
                  Цели ({scenario.objectives?.length || 0}):
                </p>
                <ul className="space-y-1">
                  {scenario.objectives?.slice(0, 2).map((obj, i) => (
                    <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                      <span className="text-blue-500">•</span>
                      <span className="line-clamp-1">{obj}</span>
                    </li>
                  ))}
                  {scenario.objectives?.length > 2 && (
                    <li className="text-xs text-gray-400">
                      +{scenario.objectives.length - 2} еще...
                    </li>
                  )}
                </ul>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleEditScenario(scenario)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm"
                >
                  <Edit2 className="w-4 h-4" />
                  Редактировать
                </button>
                <button
                  onClick={() => handleToggleActive(scenario)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors text-sm"
                >
                  <Power className="w-4 h-4" />
                  {scenario.is_active ? 'Выкл' : 'Вкл'}
                </button>
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => handleMoveUp(scenario, index)}
                  disabled={index === 0}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                    index === 0
                      ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                      : 'text-purple-600 hover:bg-purple-50'
                  }`}
                  title="Переместить вверх"
                >
                  <ArrowUp className="w-4 h-4" />
                  Вверх
                </button>
                <button
                  onClick={() => handleMoveDown(scenario, index)}
                  disabled={index === scenarios.length - 1}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                    index === scenarios.length - 1
                      ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                      : 'text-purple-600 hover:bg-purple-50'
                  }`}
                  title="Переместить вниз"
                >
                  <ArrowDown className="w-4 h-4" />
                  Вниз
                </button>
                <button
                  onClick={() => handleDelete(scenario)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Удалить
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {scenarios.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Сценарии не найдены
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingScenario ? 'Редактировать сценарий' : 'Создать сценарий'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* ID and Name */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID сценария <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.id}
                    onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                    disabled={!!editingScenario}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    placeholder="cold-call-secretary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Сложность <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="easy">Легкий</option>
                    <option value="medium">Средний</option>
                    <option value="hard">Сложный</option>
                    <option value="expert">Эксперт</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Голос AI <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.voice}
                    onChange={(e) => setFormData({ ...formData, voice: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="xenia">Ксения (Ж)</option>
                    <option value="aidar">Айдар (М)</option>
                    <option value="baya">Байя (Ж)</option>
                    <option value="kseniya">Ксюша (Ж)</option>
                    <option value="eugene">Евгений (М)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Холодный звонок секретарю"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Краткое описание сценария..."
                />
              </div>

              {/* Character Info */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Персонаж</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Роль
                    </label>
                    <input
                      type="text"
                      value={formData.characterRole}
                      onChange={(e) => setFormData({ ...formData, characterRole: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Секретарь, Директор, Менеджер..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Имя персонажа
                    </label>
                    <input
                      type="text"
                      value={formData.characterName}
                      onChange={(e) => setFormData({ ...formData, characterName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Анна Петровна"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Компания
                  </label>
                  <input
                    type="text"
                    value={formData.characterCompany}
                    onChange={(e) => setFormData({ ...formData, characterCompany: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ООО 'Северный ветер'"
                  />
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Характер персонажа
                  </label>
                  <textarea
                    value={formData.characterPersonality}
                    onChange={(e) => setFormData({ ...formData, characterPersonality: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Строгая, профессиональная, немного недоверчивая..."
                  />
                </div>
              </div>

              {/* Objectives */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Цели <span className="text-red-500">*</span>
                  </h3>
                  <button
                    type="button"
                    onClick={addObjective}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    + Добавить цель
                  </button>
                </div>
                <div className="space-y-3">
                  {formData.objectives.map((objective, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={objective}
                        onChange={(e) => updateObjective(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Пробиться к ЛПР"
                      />
                      {formData.objectives.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeObjective(index)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* System Prompt */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Системный промпт <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  value={formData.systemPrompt}
                  onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  rows={8}
                  placeholder="Вы играете роль секретаря компании..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Инструкция для AI, описывающая роль и поведение персонажа
                </p>
              </div>

              {/* Onboarding Editor */}
              <OnboardingEditor
                onboardingSteps={formData.onboardingSteps}
                onChange={(steps) => setFormData({ ...formData, onboardingSteps: steps })}
              />

              {/* Submit */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingScenario ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioManagement;
